import React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Pagination,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import NavigateBeforeIcon from "@mui/icons-material/NavigateBefore";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { format, subDays } from "date-fns";
import useConversations from "../../hooks/store/conversations";
import { getCountryNameFromCode } from "../../utils/countryLabels";

const toDateInput = (date) => format(date, "yyyy-MM-dd");
const formatDateLabel = (value) => {
  if (!value) return "";
  try {
    return format(new Date(value), "dd MMM yyyy, HH:mm");
  } catch (_) {
    return value;
  }
};

const channelLabels = {
  widget: "Widget",
  instagram: "Instagram",
  whatsapp: "WhatsApp",
  messenger: "Facebook Messenger",
};

const formatChannelLabel = (value) => {
  if (!value) return "";
  const normalized = String(value).trim().toLowerCase();
  if (channelLabels[normalized]) {
    return channelLabels[normalized];
  }
  return normalized
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const formatCountryLabel = (value) => {
  const country = typeof value === "string" ? value.trim() : "";
  return country ? getCountryNameFromCode(country) || country : "Not captured";
};

const META_CONVERSATION_CHANNELS = new Set([
  "instagram",
  "whatsapp",
  "messenger",
]);

const isMetaConversation = (thread) =>
  META_CONVERSATION_CHANNELS.has(
    String(thread?.channel || "").trim().toLowerCase()
  );

const normalizeTranscriptEvents = (messages) => {
  if (!Array.isArray(messages)) {
    return [];
  }

  return messages.flatMap((message) => {
    if (message?.type && message?.text) {
      return [
        {
          id: message.id,
          type: message.type,
          role:
            message.type === "link_click"
              ? "User clicked a link"
              : message.role ||
                (message.type === "assistant_message" ? "Assistant" : "User"),
          text: message.text,
          createdAt: message.createdAt,
          url: message.url || "",
        },
      ];
    }

    const turns = [];

    if (message?.userMessage) {
      turns.push({
        id: `${message.id}-user`,
        type: "user_message",
        role: "User",
        text: message.userMessage,
        createdAt: message.createdAt,
      });
    }

    if (message?.assistantMessage) {
      turns.push({
        id: `${message.id}-assistant`,
        type: "assistant_message",
        role: "Assistant",
        text: message.assistantMessage,
        createdAt: message.createdAt,
      });
    }

    return turns;
  });
};

const pageSizeOptions = [10, 25, 50];
const channelOptions = [
  { value: "", label: "All" },
  { value: "widget", label: "Widget" },
  { value: "instagram", label: "Instagram messages" },
  { value: "whatsapp", label: "WhatsApp messages" },
  { value: "messenger", label: "Facebook Messenger" },
];

const ConversationsPage = () => {
  const {
    businesses,
    threads,
    threadsTotal,
    selectedThread,
    messages,
    filters,
    pagination,
    ui,
    getBusinessesAction,
    getThreadsAction,
    getThreadAction,
    setFiltersAction,
    setPageNumberAction,
    setRowsPerPageAction,
    setSelectedThreadAction,
    resetAction,
  } = useConversations();

  const [localFilters, setLocalFilters] = React.useState({
    businessId: "",
    startDate: toDateInput(subDays(new Date(), 6)),
    endDate: toDateInput(new Date()),
    search: "",
  });

  React.useEffect(() => {
    resetAction();
    getBusinessesAction();
  }, [getBusinessesAction, resetAction]);

  React.useEffect(() => {
    if (!businesses.length || localFilters.businessId) {
      return;
    }

    setLocalFilters((prev) => ({
      ...prev,
      businessId: businesses[0].businessId,
    }));
  }, [businesses, localFilters.businessId]);

  const onApplyFilters = React.useCallback(() => {
    if (
      !localFilters.businessId ||
      (localFilters.startDate &&
        localFilters.endDate &&
        localFilters.startDate > localFilters.endDate)
    ) {
      return;
    }

    setPageNumberAction(0);
    const appliedFilters = {
      ...localFilters,
      channel: filters.channel || "",
    };
    setFiltersAction(appliedFilters);
    getThreadsAction({
      ...appliedFilters,
      page: 0,
      pageSize: pagination.rowsPerPage || 10,
    });
  }, [
    getThreadsAction,
    filters.channel,
    localFilters,
    pagination.rowsPerPage,
    setFiltersAction,
    setPageNumberAction,
  ]);

  const onSubmitFilters = React.useCallback(
    (event) => {
      event.preventDefault();
      onApplyFilters();
    },
    [onApplyFilters]
  );

  const selectedBusiness =
    businesses.find((business) => business.businessId === localFilters.businessId) || null;
  const hasRequestedThreads = Boolean(filters.businessId);

  const pageCount = Math.ceil(
    (threadsTotal || 0) / Math.max(1, pagination.rowsPerPage || 10)
  );
  const invalidDateRange =
    Boolean(localFilters.startDate) &&
    Boolean(localFilters.endDate) &&
    localFilters.startDate > localFilters.endDate;
  const transcriptTurns = normalizeTranscriptEvents(messages);
  const showConversationChain = isMetaConversation(selectedThread);

  const handleConversationChainNavigation = React.useCallback(
    (conversation) => {
      if (!conversation?.chatbotUserId) {
        return;
      }

      getThreadAction({
        chatbotUserId: conversation.chatbotUserId,
        businessId: conversation.businessId || selectedThread?.businessId,
      });
    },
    [getThreadAction, selectedThread?.businessId]
  );

  return (
    <Box component="main" sx={{ flexGrow: 1, py: 2 }}>
      <Container maxWidth="xl">
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="h4">Conversations</Typography>
            <Typography color="text.secondary" variant="body2">
              Admin-only conversation inspector
            </Typography>
          </Stack>

          <Card>
            <CardContent>
              <Stack
                component="form"
                direction={{ xs: "column", md: "row" }}
                spacing={2}
                onSubmit={onSubmitFilters}
              >
                <Autocomplete
                  fullWidth
                  options={businesses}
                  value={selectedBusiness}
                  getOptionLabel={(option) => option?.businessName || ""}
                  isOptionEqualToValue={(option, value) =>
                    option?.businessId === value?.businessId
                  }
                  onChange={(_, value) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      businessId: value?.businessId || "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Business" />
                  )}
                />
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={localFilters.startDate}
                  onChange={(event) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      startDate: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={localFilters.endDate}
                  onChange={(event) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      endDate: event.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: localFilters.startDate || undefined,
                  }}
                  error={invalidDateRange}
                  helperText={invalidDateRange ? "End date cannot be before start date" : ""}
                />
                <TextField
                  fullWidth
                  label="Search"
                  value={localFilters.search}
                  onChange={(event) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      search: event.target.value,
                    }))
                  }
                />
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!localFilters.businessId || invalidDateRange}
                >
                  Apply
                </Button>
              </Stack>
            </CardContent>
          </Card>

          {ui.businessesLoading && !businesses.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gap: 2,
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "minmax(0, 6fr) minmax(0, 6fr)",
                  lg: "minmax(0, 5fr) minmax(0, 7fr)",
                },
                alignItems: "start",
              }}
            >
              <Box>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ flexWrap: "wrap", rowGap: 1 }}
                      >
                        <Typography variant="h6">Threads</Typography>
                        <Stack
                          direction="row"
                          spacing={1}
                          sx={{ ml: "auto", flexShrink: 0 }}
                        >
                          <TextField
                            select
                            size="small"
                            label="Channel"
                            value={filters.channel || ""}
                            onChange={(event) => {
                              const nextChannel = event.target.value;
                              setPageNumberAction(0);
                              setFiltersAction({ channel: nextChannel });
                              if (!filters.businessId) {
                                return;
                              }
                              getThreadsAction({
                                ...filters,
                                channel: nextChannel,
                                page: 0,
                                pageSize: pagination.rowsPerPage || 10,
                              });
                            }}
                            SelectProps={{ native: true }}
                            InputLabelProps={{ shrink: true }}
                            sx={{ width: 170 }}
                          >
                            {channelOptions.map((option) => (
                              <option key={option.value || "all"} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </TextField>
                          <TextField
                            select
                            size="small"
                            label="Rows"
                            value={pagination.rowsPerPage || 10}
                            onChange={(event) => {
                              const nextSize = Number(event.target.value);
                              setRowsPerPageAction(nextSize);
                              if (!filters.businessId) {
                                return;
                              }
                              getThreadsAction({
                                ...filters,
                                page: 0,
                                pageSize: nextSize,
                              });
                            }}
                            SelectProps={{ native: true }}
                            sx={{ width: 100 }}
                          >
                            {pageSizeOptions.map((value) => (
                              <option key={value} value={value}>
                                {value}
                              </option>
                            ))}
                          </TextField>
                        </Stack>
                      </Stack>

                      {ui.threadsLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : !hasRequestedThreads ? (
                        <Typography color="text.secondary" variant="body2">
                          Apply filters to load conversations.
                        </Typography>
                      ) : threads.length ? (
                        <>
                          <List sx={{ maxHeight: 580, overflow: "auto" }}>
                            {threads.map((thread) => (
                              <React.Fragment key={thread.threadId}>
                                <ListItemButton
                                  selected={selectedThread?.threadId === thread.threadId}
                                  alignItems="flex-start"
                                  onClick={() => {
                                    setSelectedThreadAction(thread);
                                    getThreadAction({
                                      chatbotUserId:
                                        thread.chatbotUserId || thread.threadId,
                                      businessId: thread.businessId,
                                      startDate: filters.startDate,
                                      endDate: filters.endDate,
                                    });
                                  }}
                                >
                                  <ListItemText
                                    primaryTypographyProps={{ component: "div" }}
                                    secondaryTypographyProps={{ component: "div" }}
                                    primary={
                                      <Stack spacing={0.5}>
                                        <Typography
                                          variant="subtitle2"
                                          color="text.primary"
                                        >
                                          {formatDateLabel(thread.lastMessageAt)}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          {thread.messageCount} messages
                                        </Typography>
                                      </Stack>
                                    }
                                    secondary={
                                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                                        <Typography variant="body2">
                                          {thread.firstUserMessagePreview ||
                                            thread.lastUserMessagePreview ||
                                            "No user message"}
                                        </Typography>
                                        {thread.channel ? (
                                          <Typography variant="caption">
                                            {formatChannelLabel(thread.channel)}
                                          </Typography>
                                        ) : null}
                                      </Stack>
                                    }
                                  />
                                </ListItemButton>
                                <Divider />
                              </React.Fragment>
                            ))}
                          </List>
                          {pageCount > 1 ? (
                            <Box sx={{ display: "flex", justifyContent: "center" }}>
                              <Pagination
                                color="primary"
                                count={pageCount}
                                page={(pagination.page || 0) + 1}
                                onChange={(_, value) => {
                                  const nextPage = value - 1;
                                  setPageNumberAction(nextPage);
                                  getThreadsAction({
                                    ...filters,
                                    page: nextPage,
                                    pageSize: pagination.rowsPerPage || 10,
                                  });
                                }}
                              />
                            </Box>
                          ) : null}
                        </>
                      ) : (
                        <Typography color="text.secondary" variant="body2">
                          No threads found.
                        </Typography>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Card sx={{ minHeight: 640 }}>
                  <CardContent>
                    <Stack spacing={2}>
                      <Typography variant="h6">Transcript</Typography>
                      {selectedThread ? (
                        <Stack spacing={0.5}>
                          <Typography variant="subtitle2">
                            {selectedThread.businessName || selectedThread.businessId}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedThread.userId}
                          </Typography>
                          {selectedThread.channel ? (
                            <Typography variant="caption" color="text.secondary">
                              Channel: {formatChannelLabel(selectedThread.channel)}
                            </Typography>
                          ) : null}
                          <Typography variant="caption" color="text.secondary">
                            Country: {formatCountryLabel(selectedThread.country)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {selectedThread.messageCount} messages
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            First message: {formatDateLabel(selectedThread.firstMessageAt)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Last message: {formatDateLabel(selectedThread.lastMessageAt)}
                          </Typography>
                          {showConversationChain ? (
                            <Stack
                              direction={{ xs: "column", sm: "row" }}
                              spacing={1}
                              alignItems={{ xs: "flex-start", sm: "center" }}
                              sx={{ pt: 0.5 }}
                            >
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<NavigateBeforeIcon />}
                                disabled={
                                  ui.transcriptLoading ||
                                  !selectedThread.previousConversation
                                }
                                onClick={() =>
                                  handleConversationChainNavigation(
                                    selectedThread.previousConversation
                                  )
                                }
                              >
                                Previous conversation
                              </Button>
                              <Button
                                size="small"
                                variant="text"
                                endIcon={<NavigateNextIcon />}
                                disabled={
                                  ui.transcriptLoading ||
                                  !selectedThread.nextConversation
                                }
                                onClick={() =>
                                  handleConversationChainNavigation(
                                    selectedThread.nextConversation
                                  )
                                }
                              >
                                Next conversation
                              </Button>
                            </Stack>
                          ) : null}
                        </Stack>
                      ) : null}
                      <Divider />
                      {ui.transcriptLoading ? (
                        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                          <CircularProgress size={28} />
                        </Box>
                      ) : !hasRequestedThreads ? (
                        <Typography color="text.secondary" variant="body2">
                          Apply filters to load conversations.
                        </Typography>
                      ) : !selectedThread ? (
                        <Typography color="text.secondary" variant="body2">
                          Select a conversation.
                        </Typography>
                      ) : !transcriptTurns.length ? (
                        <Typography color="text.secondary" variant="body2">
                          No messages found.
                        </Typography>
                      ) : (
                        <Stack spacing={2} sx={{ maxHeight: 560, overflow: "auto" }}>
                          {transcriptTurns.map((turn) => (
                            <Box
                              key={turn.id}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                px: 2,
                                py: 1.5,
                                backgroundColor:
                                  turn.type === "user_message" ||
                                  turn.type === "link_click"
                                    ? "rgba(3, 169, 244, 0.08)"
                                    : "background.paper",
                              }}
                            >
                              <Stack spacing={1}>
                                <Typography variant="caption" color="text.secondary">
                                  {formatDateLabel(turn.createdAt)}
                                </Typography>
                                <Typography variant="subtitle2">{turn.role}</Typography>
                                {turn.type !== "link_click" ? (
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      whiteSpace: "pre-wrap",
                                      overflowWrap: "anywhere",
                                      wordBreak: "break-word",
                                      lineHeight: 1.6,
                                    }}
                                  >
                                    {turn.text}
                                  </Typography>
                                ) : null}
                                {turn.url ? (
                                  <Typography
                                    component="a"
                                    href={turn.url}
                                    target="_blank"
                                    rel="noreferrer"
                                    variant="caption"
                                    sx={{ overflowWrap: "anywhere" }}
                                  >
                                    {turn.url}
                                  </Typography>
                                ) : null}
                              </Stack>
                            </Box>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            </Box>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

export default ConversationsPage;
